import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { useAtlas } from "../store/atlasStore";
import { Play, RefreshCw, ChevronDown, ChevronRight, Trash2 } from "lucide-react";

function fmt(v, d = 3) {
  if (v == null || !isFinite(v)) return "—";
  return Number(v).toFixed(d);
}

function pct(v) {
  if (v == null) return "—";
  const n = Number(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

// ─── Confusion Matrix ───────────────────────────────────────────────────────
function ConfusionMatrix({ cm }) {
  if (!cm) return null;
  const { tp = 0, fp = 0, tn = 0, fn = 0 } = cm;
  return (
    <div style={{ marginTop: 6 }}>
      <div className="section-sep"><span className="s-label">Confusion Matrix</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 1fr", gridTemplateRows: "20px 1fr 1fr", gap: 2, marginTop: 4 }}>
        <div />
        <div style={{ textAlign: "center", fontSize: 9, color: "var(--text-tertiary)", fontWeight: 600 }}>PRED 0</div>
        <div style={{ textAlign: "center", fontSize: 9, color: "var(--text-tertiary)", fontWeight: 600 }}>PRED 1</div>
        <div style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 600, display: "flex", alignItems: "center" }}>ACT 0</div>
        <div className="conf-cell conf-tn">{tn}</div>
        <div className="conf-cell conf-fp">{fp}</div>
        <div style={{ fontSize: 9, color: "var(--text-tertiary)", fontWeight: 600, display: "flex", alignItems: "center" }}>ACT 1</div>
        <div className="conf-cell conf-fn">{fn}</div>
        <div className="conf-cell conf-tp">{tp}</div>
      </div>
    </div>
  );
}

// ─── Feature Importance ──────────────────────────────────────────────────────
function FeatureImportance({ features }) {
  if (!features?.length) return null;
  const max = features[0]?.importance ?? 1;
  return (
    <div>
      <div className="section-sep"><span className="s-label">Feature Importance (Top 15)</span></div>
      {features.slice(0, 15).map((f, i) => (
        <div className="feat-bar-row" key={i}>
          <div className="feat-bar-name" title={f.id}>{f.id}</div>
          <div className="feat-bar-track">
            <div className="feat-bar-fill" style={{ width: `${(f.importance / max) * 100}%` }} />
          </div>
          <div className="feat-bar-val">{fmt(f.importance, 4)}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Fold Table ──────────────────────────────────────────────────────────────
function FoldTable({ folds }) {
  if (!folds?.length) return null;
  return (
    <div>
      <div className="section-sep"><span className="s-label">Walk-Forward Folds</span></div>
      <div className="table-container" style={{ maxHeight: 160 }}>
        <table className="dt" style={{ fontSize: 10 }}>
          <thead>
            <tr>
              <th>Fold</th>
              <th>Train</th>
              <th>Test</th>
              <th className="r">Acc</th>
              <th className="r">LogLoss</th>
              <th className="r">IC</th>
              <th className="r">F1</th>
              <th className="r">AUC</th>
            </tr>
          </thead>
          <tbody>
            {folds.map((f, i) => (
              <tr key={i}>
                <td><span className="badge">{i}</span></td>
                <td className="label-cell" style={{ fontSize: 9 }}>
                  {f.train_start?.slice(0,10)} → {f.train_end?.slice(0,10)}<br />
                  <span style={{ color: "var(--text-tertiary)" }}>({f.n_train} bars)</span>
                </td>
                <td className="label-cell" style={{ fontSize: 9 }}>
                  {f.test_start?.slice(0,10)} → {f.test_end?.slice(0,10)}<br />
                  <span style={{ color: "var(--text-tertiary)" }}>({f.n_test} bars)</span>
                </td>
                <td className="r">{fmt(f.metrics?.accuracy, 3)}</td>
                <td className="r">{fmt(f.metrics?.log_loss, 3)}</td>
                <td className="r" style={{ color: (f.metrics?.information_coefficient ?? 0) > 0 ? "var(--green)" : "var(--red)" }}>
                  {fmt(f.metrics?.information_coefficient, 4)}
                </td>
                <td className="r">{fmt(f.metrics?.f1, 3)}</td>
                <td className="r">{fmt(f.metrics?.roc_auc, 3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Experiment Row (collapsible) ────────────────────────────────────────────
function ExpRow({ exp, onSelect, selected }) {
  const [open, setOpen] = useState(false);
  const agg = exp.aggregate || {};
  return (
    <>
      <tr
        className={selected ? "selected" : ""}
        style={{ cursor: "pointer" }}
        onClick={() => { onSelect(exp); setOpen(o => !o); }}
      >
        <td><span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
          {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          <span className="mono" style={{ fontSize: 10 }}>{exp.experiment_id?.slice(0,8)}</span>
        </span></td>
        <td className="label-cell" style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
          {exp.spec?.name || "—"}
        </td>
        <td><span className="badge badge-violet">{exp.spec?.model?.kind}</span></td>
        <td className="r">{exp.spec?.dataset?.feature_ids?.length || 0}</td>
        <td className="r">{fmt(agg.accuracy_mean, 3)}</td>
        <td className="r" style={{ color: (agg.information_coefficient_mean ?? 0) > 0 ? "var(--green)" : "var(--red)" }}>
          {fmt(agg.information_coefficient_mean, 4)}
        </td>
        <td className="r">{fmt(agg.roc_auc_mean, 3)}</td>
        <td className="r">{exp.folds?.length || 0}</td>
        <td className="label-cell" style={{ fontSize: 9 }}>{exp.created_at?.slice(0,16).replace("T"," ")}</td>
      </tr>
    </>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export function ResearchWorkspace() {
  const { state } = useAtlas();
  const { provider, symbol, interval } = state;

  // Experiment config
  const [modelKind, setModelKind] = useState("randomforest");
  const [featureSet, setFeatureSet] = useState("core");
  const [labelKind, setLabelKind] = useState("sign");
  const [horizon, setHorizon] = useState(5);
  const [nFolds, setNFolds] = useState(3);
  const [period, setPeriod] = useState("3y");

  // State
  const [allFeatures, setAllFeatures] = useState([]);
  const [meta, setMeta] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [models, setModels] = useState([]);
  const [running, setRunning] = useState(false);
  const [expResult, setExpResult] = useState(null);
  const [selectedExp, setSelectedExp] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [inferResult, setInferResult] = useState(null);
  const [inferLoading, setInferLoading] = useState(false);
  const [btResult, setBtResult] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [sortBy, setSortBy] = useState("accuracy_mean");

  const refresh = useCallback(async () => {
    try {
      const [expR, modR] = await Promise.all([
        axios.get(`${API}/learning/experiments`, { params: { limit: 30 } }),
        axios.get(`${API}/learning/models`, { params: { limit: 50, provider, symbol } }),
      ]);
      setExperiments(expR.data);
      setModels(modR.data);
    } catch {}
  }, [provider, symbol]);

  useEffect(() => {
    axios.get(`${API}/learning/`).then(r => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/registry/features`).then(r => setAllFeatures(r.data.map(f => f.id))).catch(() => {});
    refresh();
  }, [refresh]);

  const FEATURE_SETS = {
    core:     { label: "Core (8)",    ids: () => ["price.motion.log_return_5","price.motion.log_return_20","price.statistics.zscore_20","price.regime.adx_14","price.position.pct_rank_50","price.expansion.bb_width_20_k2","price.geometry.hurst_64","price.entropy.perm_o3_w30"] },
    extended: { label: "Extended (68)", ids: () => allFeatures.slice(0, 68) },
    random:   { label: "Random (15)", ids: () => [...allFeatures].sort(() => Math.random()-0.5).slice(0,15) },
    all:      { label: `All (${allFeatures.length || 827})`, ids: () => allFeatures },
  };

  const runExperiment = async () => {
    setRunning(true);
    setExpResult(null);
    try {
      const ids = FEATURE_SETS[featureSet]?.ids() || allFeatures;
      const body = {
        name: `${modelKind}-${symbol}-${Date.now().toString(36).slice(-4)}`,
        provider, symbol, interval, period,
        dataset: { feature_ids: ids, horizon, label_kind: labelKind },
        split: { kind: "walk_forward", n_folds: nFolds, train_size: 0.6 },
        model: { kind: modelKind, seed: Math.floor(Math.random()*99999) },
        tags: ["ui"],
      };
      const r = await axios.post(`${API}/learning/experiments/run`, body, { timeout: 300_000 });
      setExpResult(r.data);
      setSelectedExp(r.data);
      await refresh();
    } catch (e) {
      setExpResult({ error: e.response?.data?.detail || e.message });
    }
    setRunning(false);
  };

  const runInference = async () => {
    if (!selectedModel) return;
    setInferLoading(true);
    setInferResult(null);
    try {
      const r = await axios.post(`${API}/learning/inference/predict`, {
        model_id: selectedModel.model_id, provider, symbol, interval, period: "3y",
      });
      setInferResult(r.data);
    } catch (e) { setInferResult({ error: e.message }); }
    setInferLoading(false);
  };

  const runBacktest = async () => {
    if (!selectedModel) return;
    setBtLoading(true);
    setBtResult(null);
    try {
      const r = await axios.post(`${API}/learning/backtest/run`, {
        model_id: selectedModel.model_id, provider, symbol, interval, period: "3y",
        warmup_bars: 150, initial_cash: 100000, position_size_pct: 20.0,
        stop_loss_pct: 2.0, take_profit_pct: 4.0
      }, { timeout: 300_000 });
      setBtResult(r.data);
    } catch (e) { setBtResult({ error: e.message }); }
    setBtLoading(false);
  };

  const handleDelete = async (e, m) => {
    e.stopPropagation();
    if (!window.confirm(`Delete model ${m.model_id}?`)) return;
    try {
      await axios.delete(`${API}/learning/models/${m.model_id}`);
      if (selectedModel?.model_id === m.model_id) setSelectedModel(null);
      refresh();
    } catch (err) {
      alert("Failed to delete model: " + (err.response?.data?.detail || err.message));
    }
  };

  const activeExp = selectedExp || expResult;
  const kinds = meta?.registered_models || ["logreg","xgboost","randomforest","lightgbm","gradientboost"];

  // Sort models
  const sortedModels = [...models].sort((a, b) => {
    const va = a.latest_backtest?.[sortBy] ?? a.metrics?.[sortBy] ?? -9999;
    const vb = b.latest_backtest?.[sortBy] ?? b.metrics?.[sortBy] ?? -9999;
    return vb - va;
  });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr 240px", gridTemplateRows: "1fr 220px", height: "100%", gap: 4, padding: 4 }}>

      {/* ── LEFT: Experiment Launcher ──────────────────────────────────── */}
      <div className="panel" style={{ gridRow: "1 / 3", display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Experiment Launcher</span>
          <span className="badge badge-blue">v{meta?.version || "0.13"}</span>
        </div>
        <div className="panel-body" style={{ overflowY: "auto" }}>

          <div className="section-sep"><span className="s-label">Configuration</span></div>

          {/* Model */}
          <div style={{ marginBottom: 8 }}>
            <div className="label" style={{ marginBottom: 3 }}>Model Type</div>
            <select className="inp" value={modelKind} onChange={e => setModelKind(e.target.value)}>
              {kinds.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>

          {/* Feature Set */}
          <div style={{ marginBottom: 8 }}>
            <div className="label" style={{ marginBottom: 3 }}>Feature Set</div>
            <select className="inp" value={featureSet} onChange={e => setFeatureSet(e.target.value)}>
              {Object.entries(FEATURE_SETS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          {/* Label + Horizon */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
            <div>
              <div className="label" style={{ marginBottom: 3 }}>Label</div>
              <select className="inp" value={labelKind} onChange={e => setLabelKind(e.target.value)}>
                {(meta?.registered_label_kinds || ["sign","quantile","vol_norm","triple_barrier"]).map(k => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="label" style={{ marginBottom: 3 }}>Horizon (bars)</div>
              <input type="number" className="inp" value={horizon} min={1} max={60}
                onChange={e => setHorizon(+e.target.value || 5)} />
            </div>
          </div>

          {/* Folds + Period */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 12 }}>
            <div>
              <div className="label" style={{ marginBottom: 3 }}>WF Folds</div>
              <input type="number" className="inp" value={nFolds} min={2} max={10}
                onChange={e => setNFolds(+e.target.value || 3)} />
            </div>
            <div>
              <div className="label" style={{ marginBottom: 3 }}>Period</div>
              <select className="inp" value={period} onChange={e => setPeriod(e.target.value)}>
                {["1y","2y","3y","5y"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <button
            className="btn btn-primary"
            style={{ width: "100%", justifyContent: "center", height: 32, fontSize: 12 }}
            onClick={runExperiment}
            disabled={running}
          >
            {running ? <RefreshCw size={12} className="spin" /> : <Play size={12} />}
            {running ? "Training…" : "Run Walk-Forward Experiment"}
          </button>

          {/* Quick result summary */}
          {expResult && !expResult.error && (
            <div style={{ marginTop: 10, padding: 8, background: "var(--green-muted)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                <div className="pulse-dot" />
                <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--green)" }}>
                  Training Complete
                </span>
              </div>
              {[
                ["Acc", fmt(expResult.aggregate?.accuracy_mean)],
                ["IC",  fmt(expResult.aggregate?.information_coefficient_mean, 4)],
                ["AUC", fmt(expResult.aggregate?.roc_auc_mean)],
                ["F1",  fmt(expResult.aggregate?.f1_mean)],
                ["Feats", expResult.dataset_meta?.n_features],
                ["Rows",  expResult.dataset_meta?.n_rows],
                ["Folds", expResult.folds?.length],
                ["Duration", `${expResult.duration_seconds?.toFixed(1)}s`],
              ].map(([k, v]) => (
                <div className="kv-row" key={k}><span className="k">{k}</span><span className="v">{v}</span></div>
              ))}
            </div>
          )}

          {expResult?.error && (
            <div style={{ marginTop: 10, padding: 8, background: "var(--red-muted)", border: "1px solid rgba(244,63,94,0.25)", borderRadius: 4, color: "var(--red)", fontSize: 10 }}>
              Error: {expResult.error}
            </div>
          )}

          {/* Selected Model Actions */}
          {selectedModel && (
            <div style={{ marginTop: 10 }}>
              <div className="section-sep"><span className="s-label">Active Model</span></div>
              <div style={{ padding: 6, background: "var(--bg-raised)", borderRadius: 3, marginBottom: 6, fontSize: 10 }}>
                <div className="mono" style={{ color: "var(--blue)" }}>{selectedModel.model_id?.slice(0,16)}</div>
                <div style={{ color: "var(--text-tertiary)", marginTop: 2 }}>{selectedModel.kind} · {selectedModel.metrics?.accuracy_mean?.toFixed(3)}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                <button className="btn" style={{ justifyContent: "center", fontSize: 10 }} onClick={runInference} disabled={inferLoading}>
                  {inferLoading ? <RefreshCw size={10} className="spin" /> : null}
                  {inferLoading ? "…" : "Inference"}
                </button>
                <button className="btn btn-primary" style={{ justifyContent: "center", fontSize: 10 }} onClick={runBacktest} disabled={btLoading}>
                  {btLoading ? <RefreshCw size={10} className="spin" /> : null}
                  {btLoading ? "…" : "Backtest"}
                </button>
              </div>

              {inferResult && !inferResult.error && (
                <div style={{ marginTop: 6, padding: 6, background: "var(--bg-raised)", borderRadius: 3, fontSize: 10 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span className={`dir-pill ${inferResult.direction === "UP" ? "up" : inferResult.direction === "DOWN" ? "down" : "flat"}`}
                      style={{ fontSize: 10 }}>
                      {inferResult.direction}
                    </span>
                    <span style={{ color: "var(--text-secondary)" }}>P(Up): <span className="mono">{((inferResult.probability_up ?? 0.5)*100).toFixed(1)}%</span></span>
                    <span style={{ color: "var(--text-secondary)" }}>Conf: <span className="mono">{((inferResult.confidence ?? 0)*100).toFixed(0)}%</span></span>
                  </div>
                </div>
              )}

              {btResult && !btResult.error && (
                <div style={{ marginTop: 6, padding: 6, background: "var(--bg-raised)", borderRadius: 3 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                    {[
                      ["Return", pct(btResult.metrics?.total_return_pct), btResult.metrics?.total_return_pct >= 0 ? "var(--green)" : "var(--red)"],
                      ["Sharpe", fmt(btResult.metrics?.sharpe), "var(--text-primary)"],
                      ["MaxDD",  `${fmt(btResult.metrics?.max_drawdown_pct)}%`, "var(--red)"],
                      ["Trades", btResult.metrics?.n_trades, "var(--text-primary)"],
                    ].map(([k, v, c]) => (
                      <div key={k} style={{ padding: 4, background: "var(--bg-overlay)", borderRadius: 3 }}>
                        <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase" }}>{k}</div>
                        <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── CENTER: Experiment Analysis ────────────────────────────────── */}
      <div className="panel" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div className="panel-header">
          <span className="panel-title">
            {activeExp ? `Experiment Analysis — ${activeExp.experiment_id?.slice(0,8)}` : "Experiment Analysis"}
          </span>
          {activeExp && (
            <div style={{ display: "flex", gap: 4 }}>
              <span className="badge">{activeExp.spec?.model?.kind}</span>
              <span className="badge badge-blue">{activeExp.dataset_meta?.n_features ?? activeExp.spec?.dataset?.feature_ids?.length ?? 0} feats</span>
              <span className="badge">{activeExp.dataset_meta?.n_rows ?? 0} rows</span>
            </div>
          )}
        </div>
        <div className="panel-body" style={{ overflowY: "auto" }}>
          {!activeExp && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--text-tertiary)", fontSize: 11 }}>
              Run an experiment or select one from history
            </div>
          )}
          {activeExp && !activeExp.error && (
            <div>
              {/* Aggregate Metrics Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 4, marginBottom: 10 }}>
                {[
                  { label: "Accuracy",    v: fmt(activeExp.aggregate?.accuracy_mean), sub: `±${fmt(activeExp.aggregate?.accuracy_std, 4)}` },
                  { label: "IC",          v: fmt(activeExp.aggregate?.information_coefficient_mean, 4), sub: `±${fmt(activeExp.aggregate?.information_coefficient_std, 4)}`, col: (activeExp.aggregate?.information_coefficient_mean ?? 0) > 0 ? "var(--green)" : "var(--red)" },
                  { label: "ROC AUC",     v: fmt(activeExp.aggregate?.roc_auc_mean), sub: `±${fmt(activeExp.aggregate?.roc_auc_std, 4)}` },
                  { label: "LogLoss",     v: fmt(activeExp.aggregate?.log_loss_mean), sub: "" },
                  { label: "F1",          v: fmt(activeExp.aggregate?.f1_mean), sub: "" },
                  { label: "Precision",   v: fmt(activeExp.aggregate?.precision_mean), sub: "" },
                  { label: "Recall",      v: fmt(activeExp.aggregate?.recall_mean), sub: "" },
                  { label: "Brier",       v: fmt(activeExp.aggregate?.brier_mean), sub: "" },
                ].map(m => (
                  <div key={m.label} className="stat-card" style={{ padding: "6px 8px" }}>
                    <div className="s-label">{m.label}</div>
                    <div className="s-val" style={{ fontSize: 14, color: m.col || "var(--text-primary)" }}>{m.v}</div>
                    {m.sub && <div className="s-sub">{m.sub}</div>}
                  </div>
                ))}
              </div>

              <FoldTable folds={activeExp.folds} />

              {activeExp.folds?.[0]?.top_features?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <FeatureImportance features={activeExp.folds[0].top_features} />
                </div>
              )}

              {activeExp.folds?.[0]?.metrics?.confusion_matrix && (
                <ConfusionMatrix cm={activeExp.folds[0].metrics.confusion_matrix} />
              )}
            </div>
          )}
          {activeExp?.error && (
            <div style={{ color: "var(--red)", fontSize: 11, padding: 8 }}>Error: {activeExp.error}</div>
          )}
        </div>
      </div>

      {/* ── RIGHT: Model Registry ──────────────────────────────────────── */}
      <div className="panel" style={{ gridRow: "1 / 3", display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Model Registry</span>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span className="badge">{models.length} models</span>
            <button className="btn btn-ghost btn-icon" onClick={refresh}><RefreshCw size={11} /></button>
          </div>
        </div>
        <div style={{ padding: "4px 8px", borderBottom: "1px solid var(--border-dim)", flexShrink: 0 }}>
          <select className="inp" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            {["total_return_pct","sharpe","accuracy_mean","information_coefficient_mean","roc_auc_mean","f1_mean","brier_mean"].map(k => (
              <option key={k} value={k}>{k.replace(/_/g," ")}</option>
            ))}
          </select>
        </div>
        <div className="table-container" style={{ flex: 1 }}>
          <table className="dt">
            <thead>
              <tr>
                <th>Model</th>
                <th>Type</th>
                <th className="r">Return</th>
                <th className="r">Sharpe</th>
                <th className="r">Hit Rate</th>
                <th className="r">Acc</th>
                <th className="r">IC</th>
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedModels.map(m => (
                <tr
                  key={m.model_id}
                  style={{ cursor: "pointer" }}
                  className={selectedModel?.model_id === m.model_id ? "selected" : ""}
                  onClick={() => setSelectedModel(m)}
                >
                  <td><span className="mono" style={{ fontSize: 9, color: "var(--blue)" }}>{m.model_id?.slice(0,10)}</span></td>
                  <td><span className="badge badge-violet">{m.kind}</span></td>
                  <td className="r font-medium" style={{ color: (m.latest_backtest?.total_return_pct ?? 0) > 0 ? "var(--green)" : "var(--red)" }}>
                    {fmt(m.latest_backtest?.total_return_pct, 2)}%
                  </td>
                  <td className="r">{fmt(m.latest_backtest?.sharpe, 2)}</td>
                  <td className="r">{m.latest_backtest?.hit_rate != null ? fmt(m.latest_backtest.hit_rate * 100, 1) + '%' : '-'}</td>
                  <td className="r">{fmt(m.metrics?.accuracy_mean)}</td>
                  <td className="r" style={{ color: (m.metrics?.information_coefficient_mean ?? 0) > 0 ? "var(--green)" : "var(--red)" }}>
                    {fmt(m.metrics?.information_coefficient_mean, 4)}
                  </td>
                  <td>
                    <button className="icon-btn" style={{ color: "var(--red)" }} onClick={(e) => handleDelete(e, m)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {!models.length && (
                <tr><td colSpan={8} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: 16 }}>No models yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Selected model detail */}
        {selectedModel && (
          <div style={{ padding: 8, borderTop: "1px solid var(--border-dim)", flexShrink: 0, fontSize: 10 }}>
            <div style={{ fontWeight: 600, color: "var(--text-secondary)", marginBottom: 4 }}>Selected Model Detail</div>
            {[
              ["ID",       selectedModel.model_id?.slice(0,16)],
              ["Kind",     selectedModel.kind],
              ["Exp",      selectedModel.experiment_id?.slice(0,8)],
              ["Feats",    selectedModel.feature_ids?.length],
              ["Acc",      fmt(selectedModel.metrics?.accuracy_mean)],
              ["IC",       fmt(selectedModel.metrics?.information_coefficient_mean, 4)],
              ["AUC",      fmt(selectedModel.metrics?.roc_auc_mean)],
              ["F1",       fmt(selectedModel.metrics?.f1_mean)],
              ["Created",  selectedModel.created_at?.slice(0,10)],
            ].map(([k, v]) => v != null && (
              <div className="kv-row" key={k}><span className="k">{k}</span><span className="v">{v}</span></div>
            ))}
          </div>
        )}
      </div>

      {/* ── BOTTOM CENTER: Experiment History ─────────────────────────── */}
      <div className="panel" style={{ display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Experiment History</span>
          <span className="badge">{experiments.length} runs</span>
        </div>
        <div className="table-container">
          <table className="dt">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Model</th>
                <th className="r">Feats</th>
                <th className="r">Acc</th>
                <th className="r">IC</th>
                <th className="r">AUC</th>
                <th className="r">Folds</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {experiments.map(e => (
                <ExpRow
                  key={e.experiment_id}
                  exp={e}
                  selected={selectedExp?.experiment_id === e.experiment_id}
                  onSelect={setSelectedExp}
                />
              ))}
              {!experiments.length && (
                <tr><td colSpan={9} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: 16 }}>No experiments yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
